import type { AccessibleFleetAPI, DefaultRolesAPI, UserAPI, UserAttributesAPI, UserFleetAPI, UserPreferencesAPI, UserPreferencesSaveForm } from './ApiTypes';
import type { DefaultRole, UserAttributes, UserPreferences, UserPreferencesToSave } from './Types';
import moment from 'moment';

import type { AccessibleFleet, User, UserFleet } from '@cobliteam/common-types';

import { userOnboardingAPIToUserOnboarding } from '../../dashboard/onboarding';
import { severinoDateFormat } from '../severino';

const ONE_DAY_IN_MSECS = 86400000;

const cobliEmailFilter = (userApi: UserAPI) => {
  return !userApi.email_address.match('frota-admin.*@cobli.co');
};

const dateStrToTimestampInMillis = (dateStr?: string) => {
  return dateStr ? moment(dateStr, severinoDateFormat).valueOf() : undefined;
};

export const userFleetsApiToUserFleets = (accessibleFleets: UserFleetAPI[]): UserFleet[] => {
  return accessibleFleets.map((accessibleFleet) => ({
    fleetId: accessibleFleet.fleet_id,
    userId: accessibleFleet.user_id,
    groupsRestriction: accessibleFleet.groups_restriction,
    role: accessibleFleet.role,
  }));
};

export const accessibleFleetsApiToAccessibeFleets = (accessibleFleets: AccessibleFleetAPI[]): AccessibleFleet[] => {
  return accessibleFleets.map((accessibleFleet) => ({
    id: accessibleFleet.id,
    name: accessibleFleet.name,
    associatedAt: dateStrToTimestampInMillis(accessibleFleet.associated_at),
  }));
};

const userApiToUser = (user: UserAPI): User => {
  return {
    id: user.id,
    name: user.full_name,
    role: user.role,
    lastLogin: dateStrToTimestampInMillis(user.last_login),
    email: user.email_address,
    creationDate: dateStrToTimestampInMillis(user.creation_date),
    groupsRestriction: user.groups_restriction,
    grantedPermissions: user.granted_permissions,
    hasCustomPermissions: user.has_custom_permissions,
    active: user.active,
  };
};

export const usersApiToUsers = (usersApi: UserAPI[]) => {
  return usersApi.filter(cobliEmailFilter).map((user) => userApiToUser(user));
};

export const userAttributesTransformer = (userAttributes: UserAttributesAPI): UserAttributes => {
  const creationDate = new Date(userAttributes.creation_date).getTime();

  return {
    numberOfDevices: userAttributes.num_devices,
    creationDate,
    ageInDays: Math.floor((Date.now() - creationDate) / ONE_DAY_IN_MSECS),
  };
};

export const userPreferencesSaveForm = (userPreferencesToSave: UserPreferencesToSave): UserPreferencesSaveForm => ({
  vehicle_group_ids: userPreferencesToSave.vehicleGroupIds,
  use_vehicles_cluster: userPreferencesToSave.useVehiclesCluster,
});

export const userPreferencesTransformer = (userPreferencesApi?: UserPreferencesAPI): UserPreferences | undefined => {
  if (!userPreferencesApi) return undefined;
  return {
    id: userPreferencesApi.id,
    vehicleGroupIds: userPreferencesApi.vehicle_group_ids,
    useVehiclesCluster: userPreferencesApi.use_vehicles_cluster,
    onboarding: userOnboardingAPIToUserOnboarding(userPreferencesApi.onboarding),
  };
};

export const defaultRolesTransformer = (defaultRolesApi: DefaultRolesAPI): DefaultRole[] => defaultRolesApi.default_roles;
